import { useEffect, useRef, useState } from "react";
import { Loader2, Plus, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fileUrl } from "../../lib/api";
import { PageHeader, StatusBadge, LoadingRows, EmptyState } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

const Field = ({ label, children }) => <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;

function ImageInput({ value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  return (
    <div className="flex gap-2">
      <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder="Image URL or upload →" className="flex-1" />
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={async (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        setBusy(true);
        try {
          const fd = new FormData(); fd.append("file", f);
          const { data } = await api.post("/uploads", fd);
          onDone_url(data, onChange); toast.success("Image uploaded");
        } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
      }} />
      <Button type="button" variant="outline" size="icon" data-testid={`${testid}-upload`} onClick={() => ref.current?.click()} disabled={busy}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
      </Button>
    </div>
  );
}
const onDone_url = (data, onChange) => onChange(data.url || "");

const EMPTY_SERVICE = { title: "", category: "", description: "", tags: "", image: "", basic: { price: "", delivery_days: 3, revisions: 1, desc: "" }, standard: null, premium: null };
const EMPTY_PRODUCT = { title: "", category: "", description: "", price: "", offer_price: "", license: "", version: "1.0", image: "" };
const EMPTY_COURSE = { title: "", category: "", description: "", price: "", offer_price: "", level: "beginner", language: "English", thumbnail: "", modules: [] };

export default function FreelancerListings() {
  const [cats, setCats] = useState([]);
  const [services, setServices] = useState(null);
  const [products, setProducts] = useState(null);
  const [courses, setCourses] = useState(null);
  const [dlg, setDlg] = useState(null); // 'service' | 'product' | 'course'
  const [sForm, setSForm] = useState(EMPTY_SERVICE);
  const [pForm, setPForm] = useState(EMPTY_PRODUCT);
  const [cForm, setCForm] = useState(EMPTY_COURSE);
  const [busy, setBusy] = useState(false);
  const [uploadFor, setUploadFor] = useState(null);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/mkt/freelancer/services").then((r) => setServices(r.data.services)).catch((e) => { toast.error(errMsg(e)); setServices([]); });
    api.get("/mkt/freelancer/products").then((r) => setProducts(r.data.products)).catch(() => setProducts([]));
    api.get("/mkt/freelancer/courses").then((r) => setCourses(r.data.courses)).catch(() => setCourses([]));
  };
  useEffect(() => { load(); api.get("/mkt/categories").then((r) => setCats(r.data.categories)).catch(() => {}); }, []);

  const CatSelect = ({ value, onChange, testid }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Select category" /></SelectTrigger>
      <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
    </Select>
  );

  const saveService = async (status) => {
    setBusy(true);
    try {
      const packages = { basic: { ...sForm.basic, price: Number(sForm.basic.price) } };
      if (sForm.standard?.price) packages.standard = { ...sForm.standard, price: Number(sForm.standard.price) };
      if (sForm.premium?.price) packages.premium = { ...sForm.premium, price: Number(sForm.premium.price) };
      await api.post("/mkt/freelancer/services", {
        title: sForm.title, category: sForm.category, description: sForm.description, status,
        tags: sForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
        keywords: sForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
        images: sForm.image ? [sForm.image] : [], packages,
      });
      toast.success(status === "published" ? "Service published!" : "Service saved as draft");
      setDlg(null); setSForm(EMPTY_SERVICE); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const saveProduct = async (status) => {
    setBusy(true);
    try {
      await api.post("/mkt/freelancer/products", {
        title: pForm.title, category: pForm.category, description: pForm.description, status,
        price: Number(pForm.price), offer_price: pForm.offer_price ? Number(pForm.offer_price) : undefined,
        license: pForm.license, version: pForm.version, images: pForm.image ? [pForm.image] : [],
      });
      toast.success(status === "published" ? "Product published!" : "Product saved as draft");
      setDlg(null); setPForm(EMPTY_PRODUCT); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const saveCourse = async (status) => {
    setBusy(true);
    try {
      await api.post("/mkt/freelancer/courses", {
        title: cForm.title, category: cForm.category, description: cForm.description, status,
        price: Number(cForm.price), offer_price: cForm.offer_price ? Number(cForm.offer_price) : undefined,
        level: cForm.level, language: cForm.language, thumbnail: cForm.thumbnail, modules: cForm.modules,
      });
      toast.success(status === "published" ? "Course published!" : "Course saved as draft");
      setDlg(null); setCForm(EMPTY_COURSE); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const toggleStatus = async (kind, id, cur) => {
    const next = cur === "published" ? "paused" : "published";
    try {
      await api.patch(`/mkt/freelancer/${kind}/${id}`, { status: next });
      toast.success(next === "published" ? "Published" : "Paused"); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const uploadFile = async (e) => {
    const f = e.target.files?.[0]; if (!f || !uploadFor) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post(`/mkt/freelancer/products/${uploadFor}/files`, fd);
      toast.success("File attached — buyers get it after payment"); setUploadFor(null); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
  };

  const Row = ({ kind, item, extra }) => (
    <div data-testid={`listing-${item.id}`} className="bg-card border border-border rounded-xl p-4 flex flex-wrap items-center gap-4">
      {(item.images?.[0] || item.thumbnail)
        ? <img src={fileUrl(item.images?.[0] || item.thumbnail)} alt="" className="h-12 w-16 rounded-lg object-cover shrink-0" />
        : <div className="h-12 w-16 rounded-lg shrink-0" style={{ background: "linear-gradient(135deg,#007A4D,#2563EB)" }} />}
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-display font-bold text-sm">{item.title}</p>
          <StatusBadge status={item.status} />
          {item.featured && <span className="text-[10px] font-bold text-amber-500 uppercase">Featured</span>}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{item.category} · {item.sales || 0} sold · ★ {item.rating || 0}{extra || ""}</p>
      </div>
      <div className="flex gap-2">
        {kind === "products" && (
          <Button size="sm" variant="outline" data-testid={`listing-upload-${item.id}`} onClick={() => { setUploadFor(item.id); fileRef.current?.click(); }} className="rounded-full font-bold">
            <Upload className="h-3.5 w-3.5 mr-1" />File ({(item.files || []).length})
          </Button>)}
        <Button size="sm" variant={item.status === "published" ? "outline" : "default"} data-testid={`listing-toggle-${item.id}`}
          onClick={() => toggleStatus(kind, item.id, item.status)}
          className={`rounded-full font-bold ${item.status === "published" ? "" : "gradient-bg text-white border-0"}`}>
          {item.status === "published" ? "Pause" : "Publish"}
        </Button>
      </div>
    </div>
  );

  const List = ({ items, kind, empty, onNew, newLabel, priceFn }) => (
    !items ? <LoadingRows rows={3} /> : items.length === 0 ? (
      <EmptyState title={empty} body="Click below to create your first listing.">
        <Button data-testid={`new-${kind}-empty`} onClick={onNew} className="rounded-full font-bold gradient-bg text-white border-0"><Plus className="h-4 w-4 mr-1" />{newLabel}</Button>
      </EmptyState>
    ) : (
      <div className="space-y-3">
        <div className="flex justify-end">
          <Button data-testid={`new-${kind}`} onClick={onNew} size="sm" className="rounded-full font-bold gradient-bg text-white border-0"><Plus className="h-4 w-4 mr-1" />{newLabel}</Button>
        </div>
        {items.map((it) => <Row key={it.id} kind={kind} item={it} />)}
      </div>
    )
  );

  const PkgFields = ({ pkg, label, optional }) => {
    const on = !!sForm[pkg];
    return (
      <div className="rounded-xl border border-border p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="font-bold text-sm capitalize">{label}</p>
          {optional && (
            <button type="button" data-testid={`pkg-toggle-${pkg}`} onClick={() => setSForm({ ...sForm, [pkg]: on ? null : { price: "", delivery_days: 5, revisions: 2, desc: "" } })}
              className={`text-xs font-bold px-3 py-1 rounded-full ${on ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>{on ? "Enabled" : "Add"}</button>)}
        </div>
        {on && (
          <div className="grid grid-cols-3 gap-3">
            <Field label="Price (₹)"><Input data-testid={`pkg-${pkg}-price`} type="number" min="1" value={sForm[pkg].price} onChange={(e) => setSForm({ ...sForm, [pkg]: { ...sForm[pkg], price: e.target.value } })} /></Field>
            <Field label="Days"><Input data-testid={`pkg-${pkg}-days`} type="number" min="1" value={sForm[pkg].delivery_days} onChange={(e) => setSForm({ ...sForm, [pkg]: { ...sForm[pkg], delivery_days: e.target.value } })} /></Field>
            <Field label="Revisions"><Input data-testid={`pkg-${pkg}-revisions`} type="number" min="0" value={sForm[pkg].revisions} onChange={(e) => setSForm({ ...sForm, [pkg]: { ...sForm[pkg], revisions: e.target.value } })} /></Field>
          </div>)}
        {on && <Field label="Package summary"><Input data-testid={`pkg-${pkg}-desc`} value={sForm[pkg].desc} onChange={(e) => setSForm({ ...sForm, [pkg]: { ...sForm[pkg], desc: e.target.value } })} placeholder={`What's included in ${label}?`} /></Field>}
      </div>
    );
  };

  return (
    <div data-testid="freelancer-listings-page">
      <PageHeader title="My Listings" subtitle="Services, digital products and courses you sell" testid="listings-title" />
      <input ref={fileRef} type="file" className="hidden" onChange={uploadFile} />

      <Tabs defaultValue="services">
        <TabsList data-testid="listings-tabs" className="flex-wrap h-auto">
          <TabsTrigger value="services" data-testid="listings-tab-services">Services ({services?.length ?? "…"})</TabsTrigger>
          <TabsTrigger value="products" data-testid="listings-tab-products">Products ({products?.length ?? "…"})</TabsTrigger>
          <TabsTrigger value="courses" data-testid="listings-tab-courses">Courses ({courses?.length ?? "…"})</TabsTrigger>
        </TabsList>
        <TabsContent value="services" className="mt-5">
          <List items={services} kind="services" empty="No services yet" newLabel="New Service" onNew={() => { setSForm(EMPTY_SERVICE); setDlg("service"); }} />
        </TabsContent>
        <TabsContent value="products" className="mt-5">
          <List items={products} kind="products" empty="No digital products yet" newLabel="New Product" onNew={() => { setPForm(EMPTY_PRODUCT); setDlg("product"); }} />
        </TabsContent>
        <TabsContent value="courses" className="mt-5">
          <List items={courses} kind="courses" empty="No courses yet" newLabel="New Course" onNew={() => { setCForm(EMPTY_COURSE); setDlg("course"); }} />
        </TabsContent>
      </Tabs>

      {/* SERVICE DIALOG */}
      <Dialog open={dlg === "service"} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="service-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">New Service</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Title"><Input data-testid="service-title" value={sForm.title} onChange={(e) => setSForm({ ...sForm, title: e.target.value })} placeholder="I will design a premium logo…" /></Field>
              <Field label="Category"><CatSelect testid="service-category" value={sForm.category} onChange={(v) => setSForm({ ...sForm, category: v })} /></Field>
            </div>
            <Field label="Description"><Textarea data-testid="service-description" value={sForm.description} onChange={(e) => setSForm({ ...sForm, description: e.target.value })} rows={4} /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Tags (comma separated)"><Input data-testid="service-tags" value={sForm.tags} onChange={(e) => setSForm({ ...sForm, tags: e.target.value })} placeholder="logo, branding, minimal" /></Field>
              <Field label="Cover image"><ImageInput testid="service-image" value={sForm.image} onChange={(v) => setSForm({ ...sForm, image: v })} /></Field>
            </div>
            <PkgFields pkg="basic" label="Basic" />
            <PkgFields pkg="standard" label="Standard" optional />
            <PkgFields pkg="premium" label="Premium" optional />
            <div className="flex gap-2">
              <Button data-testid="service-save-draft" variant="outline" onClick={() => saveService("draft")} disabled={busy} className="flex-1 rounded-full font-bold">Save Draft</Button>
              <Button data-testid="service-publish" onClick={() => saveService("published")} disabled={busy} className="flex-1 rounded-full font-bold gradient-bg text-white border-0">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PRODUCT DIALOG */}
      <Dialog open={dlg === "product"} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="product-dialog" className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">New Digital Product</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Title"><Input data-testid="product-title" value={pForm.title} onChange={(e) => setPForm({ ...pForm, title: e.target.value })} placeholder="Ultimate Notion Business Pack" /></Field>
              <Field label="Category"><CatSelect testid="product-category" value={pForm.category} onChange={(v) => setPForm({ ...pForm, category: v })} /></Field>
              <Field label="Price (₹)"><Input data-testid="product-price" type="number" min="1" value={pForm.price} onChange={(e) => setPForm({ ...pForm, price: e.target.value })} /></Field>
              <Field label="Offer price (optional)"><Input data-testid="product-offer" type="number" min="0" value={pForm.offer_price} onChange={(e) => setPForm({ ...pForm, offer_price: e.target.value })} /></Field>
              <Field label="Version"><Input data-testid="product-version" value={pForm.version} onChange={(e) => setPForm({ ...pForm, version: e.target.value })} /></Field>
              <Field label="Cover image"><ImageInput testid="product-image" value={pForm.image} onChange={(v) => setPForm({ ...pForm, image: v })} /></Field>
            </div>
            <Field label="Description"><Textarea data-testid="product-description" value={pForm.description} onChange={(e) => setPForm({ ...pForm, description: e.target.value })} rows={3} /></Field>
            <Field label="License terms"><Input data-testid="product-license" value={pForm.license} onChange={(e) => setPForm({ ...pForm, license: e.target.value })} placeholder="Personal / commercial use…" /></Field>
            <p className="text-[11px] text-muted-foreground">After creating, attach the downloadable file from the product row (File button).</p>
            <div className="flex gap-2">
              <Button data-testid="product-save-draft" variant="outline" onClick={() => saveProduct("draft")} disabled={busy} className="flex-1 rounded-full font-bold">Save Draft</Button>
              <Button data-testid="product-publish" onClick={() => saveProduct("published")} disabled={busy} className="flex-1 rounded-full font-bold gradient-bg text-white border-0">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* COURSE DIALOG */}
      <Dialog open={dlg === "course"} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="course-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">New Course</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Title"><Input data-testid="course-title" value={cForm.title} onChange={(e) => setCForm({ ...cForm, title: e.target.value })} placeholder="Digital Marketing Masterclass" /></Field>
              <Field label="Category"><CatSelect testid="course-category" value={cForm.category} onChange={(v) => setCForm({ ...cForm, category: v })} /></Field>
              <Field label="Price (₹)"><Input data-testid="course-price" type="number" min="1" value={cForm.price} onChange={(e) => setCForm({ ...cForm, price: e.target.value })} /></Field>
              <Field label="Offer price (optional)"><Input data-testid="course-offer" type="number" min="0" value={cForm.offer_price} onChange={(e) => setCForm({ ...cForm, offer_price: e.target.value })} /></Field>
              <Field label="Level">
                <Select value={cForm.level} onValueChange={(v) => setCForm({ ...cForm, level: v })}>
                  <SelectTrigger data-testid="course-level"><SelectValue /></SelectTrigger>
                  <SelectContent>{["beginner", "intermediate", "advanced"].map((l) => <SelectItem key={l} value={l} className="capitalize">{l}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Thumbnail"><ImageInput testid="course-thumbnail" value={cForm.thumbnail} onChange={(v) => setCForm({ ...cForm, thumbnail: v })} /></Field>
            </div>
            <Field label="Description"><Textarea data-testid="course-description" value={cForm.description} onChange={(e) => setCForm({ ...cForm, description: e.target.value })} rows={3} /></Field>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-bold text-sm">Curriculum</p>
                <Button type="button" size="sm" variant="outline" data-testid="course-add-module"
                  onClick={() => setCForm({ ...cForm, modules: [...cForm.modules, { title: "", lessons: [] }] })} className="rounded-full font-bold">
                  <Plus className="h-3.5 w-3.5 mr-1" />Add Module
                </Button>
              </div>
              {cForm.modules.map((m, mi) => (
                <div key={mi} className="rounded-xl border border-border p-4 space-y-3" data-testid={`course-module-${mi}`}>
                  <div className="flex gap-2">
                    <Input value={m.title} placeholder={`Module ${mi + 1} title`} data-testid={`module-title-${mi}`}
                      onChange={(e) => { const ms = [...cForm.modules]; ms[mi] = { ...m, title: e.target.value }; setCForm({ ...cForm, modules: ms }); }} />
                    <Button type="button" size="icon" variant="ghost" data-testid={`module-remove-${mi}`}
                      onClick={() => setCForm({ ...cForm, modules: cForm.modules.filter((_, i) => i !== mi) })}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  {(m.lessons || []).map((l, li) => (
                    <div key={li} className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 pl-3">
                      <Input value={l.title} placeholder={`Lesson ${li + 1} title`} data-testid={`lesson-title-${mi}-${li}`}
                        onChange={(e) => { const ms = [...cForm.modules]; const ls = [...m.lessons]; ls[li] = { ...l, title: e.target.value }; ms[mi] = { ...m, lessons: ls }; setCForm({ ...cForm, modules: ms }); }} />
                      <Input value={l.video} placeholder="Video URL (YouTube/mp4)" data-testid={`lesson-video-${mi}-${li}`}
                        onChange={(e) => { const ms = [...cForm.modules]; const ls = [...m.lessons]; ls[li] = { ...l, video: e.target.value }; ms[mi] = { ...m, lessons: ls }; setCForm({ ...cForm, modules: ms }); }} />
                      <Button type="button" size="icon" variant="ghost" data-testid={`lesson-remove-${mi}-${li}`}
                        onClick={() => { const ms = [...cForm.modules]; ms[mi] = { ...m, lessons: m.lessons.filter((_, i) => i !== li) }; setCForm({ ...cForm, modules: ms }); }}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                  <Button type="button" size="sm" variant="ghost" data-testid={`lesson-add-${mi}`}
                    onClick={() => { const ms = [...cForm.modules]; ms[mi] = { ...m, lessons: [...(m.lessons || []), { title: "", video: "", content: "" }] }; setCForm({ ...cForm, modules: ms }); }}
                    className="text-xs font-bold"><Plus className="h-3 w-3 mr-1" />Add Lesson</Button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button data-testid="course-save-draft" variant="outline" onClick={() => saveCourse("draft")} disabled={busy} className="flex-1 rounded-full font-bold">Save Draft</Button>
              <Button data-testid="course-publish" onClick={() => saveCourse("published")} disabled={busy} className="flex-1 rounded-full font-bold gradient-bg text-white border-0">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
